import React from "react";

import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import FastAPI from "../../../api/FastAPI";
import { TIME_FORMAT_WITH_DATE } from "../../../constants/dateFormat";
import prettyDurations from "../../../utils/durations";
import ReadOnlyInput from "../../form/ReadOnlyInput";

import type { JSX } from "react";

import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";
import type { InterstitialAdbreaks } from "../timeline/timeline.types";

interface AdbreakBlockProps {
  isLive?: boolean;
  interstitialAdbreaks: InterstitialAdbreaks;
}

export default function AdbreakBlock({
  isLive,
  interstitialAdbreaks,
}: AdbreakBlockProps): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const { channelId } = useParams<FASTChannelRoutesParams>();

  const getAdBreaksListQuery = FastAPI.generateQuery({
    fn: FastAPI.getAdBreaksList,
    params: [channelId],
  });

  const { data: adBreaksList } = useQuery(getAdBreaksListQuery);

  const appliedAdBreak = adBreaksList?.data.find(
    (adBreak) => adBreak.uuid === interstitialAdbreaks.adbreakId,
  );

  const adbreakDuration = prettyDurations(appliedAdBreak?.duration ?? 0, {
    valueUnit: "ms",
  }).humanised;

  const nextAdbreak = interstitialAdbreaks?.adbreakList
    .sort((a, b) => moment(a.startTime).diff(moment(b.startTime)))
    .find(
      (adbreak) =>
        moment(adbreak.startTime).isAfter(moment()) &&
        moment(adbreak.endTime).isAfter(moment()),
    );

  const nextAdbreakDate = nextAdbreak?.startTime
    ? moment(nextAdbreak?.startTime).format(TIME_FORMAT_WITH_DATE)
    : "No upcoming adbreak";

  return (
    <div className="flex flex-row gap-[0.46875rem]">
      <ReadOnlyInput
        isCompact
        label={t("appliedAdbreak")}
        name="appliedAdbreak"
        value={
          appliedAdBreak
            ? `${appliedAdBreak.name} - ${adbreakDuration}`
            : "No applied adbreak"
        }
      />
      {isLive ? (
        <ReadOnlyInput
          isCompact
          label={t("nextAdbreak")}
          name="nextAdbreak"
          value={nextAdbreakDate}
        />
      ) : null}
    </div>
  );
}
